// The IRCC forms that have a deterministic XFA mapping bundled in the pdf-fill
// service (`infra/pdf-fill/app/mappings/map_<key>.json`). Keyed by the IMM
// number found in the legalDocument's `name`.
//
// Single source of truth on purpose: this list used to be duplicated in
// `generateFill.ts` and in the console's form-filler page, so mapping an 11th
// form meant remembering two places. Add the key here (and the mapping in the
// service) and both the backend and the UI pick it up.
//
// Helper module, not a Convex function file.
export const XFA_MAPPED_FORMS = [
  "5257",
  "5710",
  "1295",
  "1344",
  "0008",
  "5406",
  "5645",
  "5409",
  "5562",
  "5475",
  "5708",
  "0008DEP",
  "1444",
  // Le recours aux services d'un représentant. Seul formulaire de la banque
  // qui se remplit avec les données du CABINET (profil représentant) et non
  // celles du client : voir `firms.representantPourRemplissage`.
  "5476",
  // Le questionnaire sur la relation du parrainage d'époux, rempli par la case
  // « couple » d'une demande. Ses questions oui/non n'ont pas toutes les mêmes
  // codes : voir build_map_5532.mjs avant d'y toucher.
  "5532",
] as const;

const XFA_MAPPED_SET = new Set<string>(XFA_MAPPED_FORMS);

/** The IMM number carried by a legalDocument name, e.g. "IMM 5257 (f)" → "5257".
 *  The 0008 annex keeps its suffix ("IMM 0008DEP" → "0008DEP"): it is its own
 *  form with its own mapping, and the bare number matched nothing at all. */
export function immKeyFromDocName(name: string | undefined): string | undefined {
  return name?.match(/\b(\d{4,5}(?:DEP)?)\b/i)?.[1]?.toUpperCase();
}

/** Does this form have a bundled deterministic mapping? */
export function isXfaMapped(immKey: string | undefined): boolean {
  return !!immKey && XFA_MAPPED_SET.has(immKey);
}

// Mapped forms whose empty boxes the AI may fill from the rest of the file
// (custom questions, other forms' answers) and whose typed answers it
// translates into the form's language. Rolled out form by form: the 5710 first.
export const AI_ASSISTED_FORMS = ["5710", "1344"] as const;

const AI_ASSISTED_SET = new Set<string>(AI_ASSISTED_FORMS);

export function isAiAssisted(immKey: string | undefined): boolean {
  return isXfaMapped(immKey) && AI_ASSISTED_SET.has(immKey!);
}

// Forms written by ONE person of a couple. The mapping reads the dossier's own
// answers ("votre nom") as that person. When the dossier belongs to the other
// person, it put their identity in the wrong section: an old "Parrainage
// (Couple)" questionnaire answered by the sponsored spouse filled the IMM 1344
// sponsor section with the spouse's own name, birth date and address.
// `roleNote` tells the AI, in the form's own terms, which answer key describes
// whom.
export const FORM_AUTHOR: Record<string, { author: "sponsor"; roleNote: string }> = {
  "1344": {
    author: "sponsor",
    roleNote: [
      "IMM 1344 is completed by the SPONSOR (the Canadian citizen or permanent resident).",
      "Keys without a prefix describe the SPONSOR: lastName, firstName, gender, dateOfBirth, birthCity, birthCountry, canadianStatus, canadianStatusObtainedDate, iucNumber, maritalStatus, marriageDate, exSpouse*, postalAddress.*, residenceAddress.*, phone.*, email, correspondenceLanguage.",
      "Keys starting with `spouse` describe the PRINCIPAL APPLICANT, the sponsored person: spouseLastName, spouseFirstName, spouseDateOfBirth. sponsoredRelationship is the sponsored person's relationship to the sponsor; relationshipStartDate is the start of the couple's relationship.",
      "Keys starting with `cosigner` describe a co-signer, a third person.",
      "canadianStatus: pick citizen by birth, by descent or naturalized only when the file says how the citizenship was obtained; a bare \"citizen\" is not enough, omit the key. sponsoredRelationship: pick the option matching where the SPONSORED person lives now according to the file; if the file does not say, omit it.",
    ].join(" "),
  },
};
